import React, { useId, useRef, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { watchCollection } from '../../firestoreLive';
import { EASE_LUSION } from './easing';
import { EXPERIENCE } from '../../constants';
import { ExperienceItem } from '../../types';
import { FadeIn } from './RevealText';
import { useTouchActive } from './useTouchActive';

/**
 * Clean CV rows — company huge, role and period in mono. Rows lift on hover
 * and open onto the CV entry underneath: the full job title, the location, and
 * what the work actually was.
 *
 * None of that detail is new. `role`, `location` and the `description` bullets
 * have always been on every record, both in the bundled constants and in every
 * Firestore document the admin panel writes. The row simply never rendered
 * them, so the section read as a list of employers rather than a CV.
 *
 * Firestore `experience` collection overrides the static constants if present.
 */

/** The reel frame's corner mark, reused as a disclosure: a plus that turns a
 *  quarter and becomes a close. Drawn rather than typed, for the same reason
 *  ArrowMark exists — the font subset cannot be trusted with either glyph. */
const PlusMark: React.FC<{ open: boolean }> = ({ open }) => (
  <motion.span
    aria-hidden="true"
    className="relative block h-3 w-3 shrink-0"
    animate={{ rotate: open ? 45 : 0 }}
    transition={{ duration: 0.45, ease: EASE_LUSION }}
  >
    <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
    <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
  </motion.span>
);

/** One experience row. Extracted so it can hold a ref: on touch there is no
 *  hover, so the row highlights when it reaches the reading band instead. */
const Row: React.FC<{ e: ExperienceItem; i: number }> = ({ e, i }) => {
  const ref = useRef<HTMLDivElement>(null);
  const active = useTouchActive(ref, '-42% 0px -42% 0px');
  const [open, setOpen] = useState(false);
  const uid = useId();
  const headerId = `${uid}-head`;
  const panelId = `${uid}-panel`;

  const bullets = e.description ?? [];
  // The admin panel can save a record carrying nothing but a company and a
  // period. A row that opens onto an empty panel is worse than one that does
  // not open at all, so those stay exactly as they were: no button, no mark,
  // nothing to press.
  const hasDetail = bullets.length > 0 || !!e.location || !!e.role;

  const cells = (
    <>
      <span className="col-span-12 md:col-span-1 font-grotesk text-xs text-ink/65 tabular-nums">
        0{i + 1}
      </span>
      <h3
        className={`col-span-12 md:col-span-5 font-grotesk font-semibold text-2xl md:text-4xl tracking-tight text-ink transition-transform duration-300 ease-out ${
          hasDetail ? 'group-hover:translate-x-2' : ''
        } ${active ? 'translate-x-2' : ''}`}
      >
        {e.company}
      </h3>
      <p className="col-span-7 md:col-span-4 font-grotesk text-xs md:text-sm uppercase tracking-[0.15em] text-ink/55">
        {e.shortDescription || e.role}
      </p>
      <p className="col-span-5 md:col-span-2 flex items-center justify-end gap-3 font-grotesk text-xs md:text-sm text-ink/65 tabular-nums whitespace-nowrap">
        {e.period}
        {hasDetail && <PlusMark open={open} />}
      </p>
    </>
  );

  const rowClass =
    'group grid w-full grid-cols-12 items-baseline gap-3 md:gap-6 py-6 md:py-8 text-left';

  return (
    <div
      ref={ref}
      className={`border-t border-ink/10 last:border-b rounded-lg md:px-4 md:-mx-4 px-3 -mx-3 transition-colors duration-300 hover:bg-white/50 ${
        active || open ? 'bg-white/50' : ''
      }`}
    >
      {hasDetail ? (
        <button
          type="button"
          id={headerId}
          aria-expanded={open}
          aria-controls={panelId}
          data-cursor={open ? 'CLOSE' : 'DETAILS'}
          onClick={() => setOpen((o) => !o)}
          className={rowClass}
        >
          {cells}
        </button>
      ) : (
        <div className={rowClass}>{cells}</div>
      )}

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={headerId}
            // height only: the rows are a grid, and animating anything that
            // affects width would drag the columns of every sibling with it
            className="overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.55, ease: EASE_LUSION }}
          >
            {/* the panel keeps the row's own 12-column grid, so the job title
                lands under the company and the bullets under the summary */}
            <div className="grid grid-cols-12 gap-3 md:gap-6 pt-1 pb-7 md:pb-10">
              <div className="col-span-12 md:col-start-2 md:col-span-5 space-y-1.5">
                {e.role && (
                  <motion.p
                    className="font-grotesk text-base md:text-lg text-ink"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1, ease: EASE_LUSION }}
                  >
                    {e.role}
                  </motion.p>
                )}
                {e.location && (
                  <motion.p
                    className="font-grotesk text-xs uppercase tracking-[0.15em] text-ink/55"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.16, ease: EASE_LUSION }}
                  >
                    {e.location}
                  </motion.p>
                )}
              </div>

              {bullets.length > 0 && (
                <ul className="col-span-12 md:col-start-7 md:col-span-6 space-y-3">
                  {bullets.map((line, b) => (
                    <motion.li
                      key={b}
                      className="flex gap-3 font-grotesk text-sm md:text-base leading-relaxed text-ink/75"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.55, delay: 0.14 + b * 0.07, ease: EASE_LUSION }}
                    >
                      <span className="mt-[0.55em] h-1 w-1 shrink-0 rounded-full bg-electric" />
                      <span>{line}</span>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ExperienceSection: React.FC = () => {
  const [items, setItems] = useState<ExperienceItem[]>(EXPERIENCE);

  useEffect(() => {
    // An empty collection keeps the bundled EXPERIENCE fallback standing
    const unsub = watchCollection<ExperienceItem>('experience', (data) => {
      if (!data.length) return;
      setItems([...data].sort((a, b) => (a.order ?? 99) - (b.order ?? 99)));
    });
    return () => unsub();
  }, []);

  return (
    <section id="experience" className="relative py-section">
      <div className="max-w-[92rem] mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between mb-10 md:mb-16">
          <h2 className="font-grotesk font-semibold text-[11vw] md:text-[5.5vw] leading-none tracking-tight text-ink">
            Experience
          </h2>
          <span className="hidden md:block font-grotesk text-xs uppercase tracking-[0.3em] text-ink/65 pb-3">
            003 · Where I've been
          </span>
        </div>

        <div>
          {items.map((e, i) => (
            <FadeIn key={`${e.company}-${i}`} delay={i * 0.04}>
              <Row e={e} i={i} />
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
